import { useQuery } from '@tanstack/react-query';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { Parameters } from '@medplum/fhirtypes';

import {
	APP_PERMISSIONS,
	permissionsQueryKey,
	hasPermission as checkPermission,
	parsePermissionsResponse,
} from '@/lib/auth/permissions';
import type { AppPermission } from '@/lib/auth/permissions';

export function usePermissions() {
	const medplum = useMedplum();
	const profile = useMedplumProfile();

	const practitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined;

	const query = useQuery({
		queryKey: permissionsQueryKey(practitionerId),
		queryFn: async () => {
			const result = (await medplum.get(
				medplum.fhirUrl('User', '$get-permissions').toString(),
			)) as Parameters;
			return parsePermissionsResponse(result);
		},
		enabled: !!practitionerId,
		staleTime: Number.POSITIVE_INFINITY,
	});

	const permissions = query.data?.permissions ?? [];
	const role = query.data?.role ?? '';
	const isSuperAdmin = permissions.includes(APP_PERMISSIONS.SUPERADMIN);

	return {
		permissions,
		role,
		hasPermission: (p: AppPermission) => checkPermission(permissions, p),
		isSuperAdmin,
		hasAdminAccess:
			isSuperAdmin ||
			checkPermission(permissions, APP_PERMISSIONS.MANAGE_USERS) ||
			checkPermission(permissions, APP_PERMISSIONS.MANAGE_ENTITIES),
		isLoading: query.isLoading,
	};
}
